import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { HERO, BRAND } from "@/lib/constants";
import { Flag, Hammer, Timer, LockOpen, Monitor, Smartphone } from "lucide-react";
import { motion } from "framer-motion";

const trustIcons = [Flag, Hammer, Timer, LockOpen];

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="bg-white pt-24 md:pt-32 pb-16 md:pb-24"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text column - 7 cols */}
          <motion.div
            className="lg:col-span-7 space-y-6"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <h1
              data-testid="hero-headline"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-tight tracking-tight"
            >
              {HERO.headline}
            </h1>

            <p
              data-testid="hero-subheadline"
              className="text-base md:text-lg text-[#6B7280] leading-relaxed max-w-2xl"
            >
              {HERO.subheadline}
            </p>

            <div className="space-y-3 pt-2">
              <Link to={BRAND.signupPath}>
                {/* Stripe checkout will connect here */}
                {/* LeadConnector webhook fires here */}
                <Button
                  data-testid="hero-cta"
                  className="bg-[#D4A84B] hover:bg-[#B88E3B] text-white font-bold h-14 px-10 rounded-md shadow-md hover:shadow-lg transition-all active:scale-95 text-base tracking-wide"
                >
                  {HERO.cta}
                </Button>
              </Link>
              <p className="text-sm text-[#6B7280]">{HERO.ctaSubtext}</p>
              <p className="text-xs text-[#6B7280]/70">{HERO.ctaSecondary}</p>
            </div>
          </motion.div>

          {/* Visual column - 5 cols */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
          >
            {/* Phone mockup (mobile) / Laptop mockup (desktop) */}
            <div
              data-testid="hero-mockup"
              className="relative max-w-md mx-auto lg:mx-0 lg:ml-auto"
            >
              {/* Phone mockup - visible on mobile/tablet only */}
              <div className="lg:hidden flex justify-center">
                <div className="relative w-56 bg-[#1A1A1A] rounded-[2rem] p-2 shadow-xl">
                  {/* Notch */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#1A1A1A] rounded-b-xl z-10" />
                  <div className="bg-[#F9FAFB] rounded-[1.5rem] aspect-[9/18] flex flex-col items-center justify-center relative overflow-hidden">
                    {/* Status bar */}
                    <div className="absolute top-0 left-0 right-0 h-8 bg-white flex items-center justify-between px-5 text-[8px] text-gray-400">
                      <span>9:41</span>
                      <span>leads.epsillonmedia.com.au</span>
                    </div>
                    {/* Dashboard placeholder */}
                    <div className="flex flex-col items-center justify-center h-full">
                      <Smartphone className="h-8 w-8 text-[#20611B]/30 mb-3" />
                      <span className="text-xs font-medium text-[#6B7280]">Dashboard Screenshot</span>
                      <span className="text-[10px] text-[#6B7280]/60 mt-1">Product preview coming soon</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Laptop mockup - visible on desktop only */}
              <div className="hidden lg:block">
                <div className="bg-[#1A1A1A] rounded-t-xl p-2">
                  <div className="bg-[#F9FAFB] rounded-lg aspect-[16/10] flex flex-col items-center justify-center relative overflow-hidden">
                    {/* Browser chrome bar */}
                    <div className="absolute top-0 left-0 right-0 h-7 bg-white border-b border-gray-200 flex items-center px-3 gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                      <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                      <span className="ml-3 flex-1 h-4 bg-gray-100 rounded text-[9px] text-gray-400 flex items-center px-2">leads.epsillonmedia.com.au</span>
                    </div>
                    {/* Dashboard placeholder */}
                    <div className="flex flex-col items-center justify-center pt-7 h-full">
                      <Monitor className="h-10 w-10 text-[#20611B]/30 mb-3" />
                      <span className="text-sm font-medium text-[#6B7280]">Dashboard Screenshot</span>
                      <span className="text-xs text-[#6B7280]/60 mt-1">Product preview coming soon</span>
                    </div>
                  </div>
                </div>
                {/* Laptop base */}
                <div className="bg-[#1A1A1A] h-3 rounded-b-sm mx-4" />
                <div className="bg-gradient-to-b from-[#2a2a2a] to-[#1A1A1A] h-1.5 rounded-b-lg mx-1" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trust bar */}
        <motion.div
          data-testid="trust-bar"
          className="mt-12 md:mt-16 pt-8 border-t border-gray-100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {HERO.trustBar.map((item, i) => {
              const Icon = trustIcons[i];
              return (
                <div
                  key={item}
                  className="flex items-center gap-2 text-sm text-[#6B7280]"
                  data-testid={`trust-item-${i}`}
                >
                  <Icon className="h-4 w-4 text-[#20611B]" />
                  <span>{item}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
